import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft,
  Calendar,
  Users,
  Clock,
  ChevronRight,
  Heart,
  GraduationCap,
  AlertCircle,
  CheckCircle,
  Timer,
  Sparkles,
  BookOpen
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProgramsPage = () => {
  const { isAuthenticated } = useAuth();
  const [programData, setProgramData] = useState(null);
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [expandedFocusAreas, setExpandedFocusAreas] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [programsRes, intakesRes] = await Promise.all([
        axios.get(`${API}/programs`),
        axios.get(`${API}/intakes/next`)
      ]);
      setProgramData(programsRes.data);
      setIntakes(intakesRes.data);
    } catch (err) {
      toast.error("Failed to load programs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredIntakes = selectedProgram
    ? intakes.filter(i => i.program_type === selectedProgram)
    : intakes;

  const getDaysUntilText = (days) => {
    if (days === 0) return "Starts today!";
    if (days === 1) return "Starts tomorrow";
    if (days <= 7) return `${days} days away`;
    if (days <= 14) return `${Math.floor(days / 7)} week away`;
    return `${Math.floor(days / 7)} weeks away`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="animate-pulse text-olive font-heading text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-off-white" data-testid="programs-page">
      {/* Header */}
      <header className="bg-white border-b border-olive/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center text-stone hover:text-olive transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <Link to="/" className="flex flex-col items-end">
            <span className="font-heading text-xl italic text-charcoal">The Pack Standard</span>
            <span className="text-olive text-xs tracking-wider">PUPPY ACADEMY</span>
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="font-heading text-4xl md:text-5xl text-charcoal mb-4">
            Our Programs
          </h1>
          <p className="text-stone text-lg max-w-2xl mx-auto">
            Choose the right program for your puppy's age and development stage.
          </p>
        </motion.div>

        {/* Program Cards */}
        {programData && (
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {programData.programs.map((program) => (
              <motion.div
                key={program.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -4 }}
                className={`card-premium cursor-pointer transition-all ${
                  selectedProgram === program.id ? "border-olive border-2" : ""
                }`}
                onClick={() => setSelectedProgram(selectedProgram === program.id ? null : program.id)}
                data-testid={`program-${program.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="text-olive">
                    {program.id === "puppy_foundations" ? (
                      <Heart className="w-10 h-10" strokeWidth={1.5} />
                    ) : (
                      <GraduationCap className="w-10 h-10" strokeWidth={1.5} />
                    )}
                  </div>
                  <Badge className="bg-olive text-white">${program.price}</Badge>
                </div>
                
                <h2 className="font-heading text-2xl text-charcoal mb-2">{program.name}</h2>
                <p className="text-olive font-medium mb-3">{program.age_range}</p>
                <p className="text-stone leading-relaxed mb-6">{program.description}</p>
                
                <div className="grid grid-cols-2 gap-4 text-sm mb-6">
                  <div className="flex items-center gap-2 text-charcoal">
                    <Calendar className="w-4 h-4 text-olive" />
                    {program.days.join(" or ")}
                  </div>
                  <div className="flex items-center gap-2 text-charcoal">
                    <Users className="w-4 h-4 text-olive" />
                    Max {program.max_puppies}
                  </div>
                  <div className="flex items-center gap-2 text-charcoal">
                    <Clock className="w-4 h-4 text-olive" />
                    {program.duration}
                  </div>
                </div>

                {/* Focus Areas */}
                <div className="border-t border-olive/10 pt-4">
                  <p className="text-sm text-stone mb-3">Focus Areas:</p>
                  <div className="flex flex-wrap gap-2">
                    {(expandedFocusAreas[program.id] ? program.focus_areas : program.focus_areas.slice(0, 5)).map((area, i) => (
                      <span key={`${program.id}-focus-${i}-${area?.title || area}`} className="text-xs bg-olive/10 text-olive px-2 py-1 rounded flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        {area?.title || (typeof area === 'string' ? area : '')}
                      </span>
                    ))}
                    {program.focus_areas.length > 5 && (
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedFocusAreas(prev => ({
                            ...prev,
                            [program.id]: !prev[program.id]
                          }));
                        }}
                        className="text-xs text-olive hover:text-olive-dark underline cursor-pointer"
                      >
                        {expandedFocusAreas[program.id] ? 'Show less' : `+${program.focus_areas.length - 5} more`}
                      </button>
                    )}
                  </div>
                </div>

                <div className="mt-6">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedProgram(selectedProgram === program.id ? null : program.id);
                      // Scroll to intakes section
                      setTimeout(() => {
                        document.getElementById('available-intakes')?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }}
                    className={`w-full ${
                      selectedProgram === program.id 
                        ? "bg-olive text-white hover:bg-olive-dark" 
                        : "btn-outline"
                    }`}
                  >
                    {selectedProgram === program.id ? "Viewing Intakes" : "View Available Intakes"}
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Three Fundamental Elements Quick View */}
        {programData?.fundamentals && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-charcoal text-white rounded-2xl p-8 mb-16"
          >
            <div className="text-center mb-8">
              <p className="text-olive uppercase tracking-[0.2em] text-sm mb-2">Our Approach</p>
              <h3 className="font-heading text-2xl">The Three Fundamental Elements</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {Object.entries(programData.fundamentals).map(([key, fundamental]) => (
                <div key={key}>
                  <div className="text-center mb-6">
                    <div className="w-12 h-12 rounded-full bg-olive/20 flex items-center justify-center mx-auto mb-4">
                      <Sparkles className="w-6 h-6 text-olive" />
                    </div>
                    <h4 className="font-heading text-xl mb-2">{fundamental.title}</h4>
                    <p className="text-olive text-sm font-medium mb-2">{fundamental.tagline}</p>
                    <p className="text-white/60 text-xs">{fundamental.description}</p>
                  </div>
                  <p className="text-white/70 text-xs mb-3">{fundamental.skills_intro}</p>
                  <ul className="space-y-2">
                    {fundamental.skills.map((skill, i) => (
                      <li key={`${fundamental.name}-skill-${i}-${skill.substring(0, 10)}`} className="text-sm">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-olive mt-0.5 flex-shrink-0" />
                          <span className="text-white/80">{skill}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <p className="text-white/50 text-xs italic mt-4 border-l-2 border-olive/50 pl-3">
                    {fundamental.goal}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Detailed Course Information */}
        {programData?.programs && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-16 space-y-12"
          >
            {programData.programs.map((program, index) => (
              <div key={program.id} className="bg-white rounded-2xl border border-olive/10 overflow-hidden">
                {/* Course Header */}
                <div className={`p-8 ${index === 0 ? 'bg-olive/5' : 'bg-terracotta/5'}`}>
                  <Badge className={index === 0 ? 'bg-olive mb-4' : 'bg-terracotta mb-4'}>
                    {program.age_range}
                  </Badge>
                  <h3 className="font-heading text-3xl text-charcoal mb-2">{program.name}</h3>
                  {program.tagline && (
                    <p className="text-olive text-lg font-medium">{program.tagline}</p>
                  )}
                </div>
                
                <div className="p-8">
                  {/* Intro */}
                  {program.intro && (
                    <p className="text-xl text-charcoal italic mb-6">{program.intro}</p>
                  )}
                  
                  {/* Description */}
                  <p className="text-stone leading-relaxed mb-8">{program.description}</p>
                  
                  {/* What They Learn */}
                  {program.what_they_learn && (
                    <div className="mb-8">
                      <h4 className="font-heading text-xl text-charcoal mb-4">
                        What Your Puppy Will Learn
                      </h4>
                      <p className="text-stone mb-4">{program.what_they_learn_intro}</p>
                      <ul className="space-y-2">
                        {program.what_they_learn.map((item, i) => (
                          <li key={`${program.id}-learn-${i}`} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-olive mt-0.5 flex-shrink-0" />
                            <span className="text-charcoal">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* How It Works (for Development course) */}
                  {program.how_it_works && (
                    <div className="mb-8 bg-sand/50 rounded-xl p-6">
                      <h4 className="font-heading text-xl text-charcoal mb-4">How It Works</h4>
                      <p className="text-stone mb-4">{program.how_it_works.intro}</p>
                      <div className="flex flex-wrap gap-3 mb-4">
                        {program.how_it_works.additions.map((item, i) => (
                          <Badge key={`${program.id}-addition-${i}`} variant="outline" className="bg-white">{item}</Badge>
                        ))}
                      </div>
                      <p className="text-charcoal italic">{program.how_it_works.outcome}</p>
                    </div>
                  )}
                  
                  {/* Workbook */}
                  {program.workbook && (
                    <div className="mb-8 bg-charcoal text-white rounded-xl p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <BookOpen className="w-6 h-6 text-olive" />
                        <h4 className="font-heading text-xl">Your Workbook (Included)</h4>
                      </div>
                      <p className="text-white/80 mb-4">You'll receive your <strong>{program.workbook.name}</strong>, which includes:</p>
                      <ul className="space-y-2 mb-4">
                        {program.workbook.includes.map((item, i) => (
                          <li key={`${program.id}-workbook-${i}`} className="flex items-start gap-3">
                            <CheckCircle className="w-4 h-4 text-olive mt-1 flex-shrink-0" />
                            <span className="text-white/80">{item}</span>
                          </li>
                        ))}
                      </ul>
                      {program.workbook.exercise_design && (
                        <div className="pt-4 border-t border-white/10">
                          <p className="text-white/60 text-sm">Each exercise is designed to be:</p>
                          <div className="flex gap-4 mt-2">
                            {program.workbook.exercise_design.map((item, i) => (
                              <span key={`${program.id}-exercise-${i}`} className="text-olive font-medium">{item}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Why It Matters (for Foundations) */}
                  {program.why_it_matters && (
                    <div className="mb-8 border-l-4 border-olive pl-6">
                      <h4 className="font-heading text-xl text-charcoal mb-2">Why This Matters</h4>
                      <p className="text-stone mb-2">{program.why_it_matters.intro}</p>
                      <p className="text-charcoal font-medium">{program.why_it_matters.outcome}</p>
                    </div>
                  )}
                  
                  {/* The Result (for Development) */}
                  {program.the_result && (
                    <div className="mb-8 border-l-4 border-terracotta pl-6">
                      <h4 className="font-heading text-xl text-charcoal mb-2">The Result</h4>
                      <p className="text-stone mb-3">{program.the_result.intro}</p>
                      <ul className="space-y-1">
                        {program.the_result.outcomes.map((item, i) => (
                          <li key={`${program.id}-result-${i}`} className="text-charcoal font-medium">{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Next Course Link (for Foundations) */}
                  {program.next_course && (
                    <div className="bg-olive/10 rounded-xl p-6">
                      <p className="text-stone text-sm mb-2">What comes next?</p>
                      <p className="text-charcoal font-heading text-lg">
                        {program.next_course.name} ({program.next_course.age})
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </motion.div>
        )}

        {/* Detailed Focus Areas by Program */}
        {programData?.programs && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-16"
          >
            {programData.programs.map((program) => (
              <div key={program.id} className="mb-12">
                <div className="text-center mb-8">
                  <p className="text-olive uppercase tracking-[0.2em] text-sm mb-2">What We Cover</p>
                  <h3 className="font-heading text-3xl text-charcoal">{program.name} Focus Areas</h3>
                  <p className="text-stone mt-2">{program.age_range}</p>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  {program.focus_areas.map((area, i) => (
                    <div key={`${program.id}-area-${i}-${area?.title || ''}`} className="bg-white rounded-xl p-6 border border-olive/10 hover:border-olive/30 transition-colors">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-olive/10 flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="w-5 h-5 text-olive" />
                        </div>
                        <div>
                          <h4 className="font-heading text-lg text-charcoal mb-2">
                            {area?.title || (typeof area === 'string' ? area : '')}
                          </h4>
                          {area?.description && (
                            <p className="text-stone text-sm leading-relaxed">{area.description}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </motion.div>
        )}

        {/* Daily Structure */}
        {programData?.daily_structure && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-sand rounded-2xl p-8 mb-16"
          >
            <div className="text-center mb-6">
              <h3 className="font-heading text-2xl text-charcoal mb-2">A Typical Academy Day</h3>
              <p className="text-olive font-medium">{programData.daily_structure.rhythm}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {programData.daily_structure.activities.slice(0, 6).map((activity, i) => (
                <div key={`activity-${i}-${activity.substring(0, 10)}`} className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-olive rounded-full" />
                  <span className="text-charcoal text-sm">{activity}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-6 border-t border-olive/20 grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-stone text-sm">Pickup Window</p>
                <p className="text-charcoal font-medium">{programData.transport.pickup_window}</p>
              </div>
              <div>
                <p className="text-stone text-sm">Drop-off Window</p>
                <p className="text-charcoal font-medium">{programData.transport.dropoff_window}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Available Intakes */}
        <motion.div
          id="available-intakes"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h2 className="font-heading text-3xl text-charcoal mb-8 text-center">
            Available Intakes
          </h2>

          {filteredIntakes.length === 0 ? (
            <div className="card-premium text-center py-12">
              <AlertCircle className="w-16 h-16 text-olive/50 mx-auto mb-4" />
              <h3 className="font-heading text-xl text-charcoal mb-2">No Intakes Available</h3>
              <p className="text-stone mb-4">
                {selectedProgram 
                  ? "No upcoming intakes for this program. Check back soon or join the waitlist."
                  : "No upcoming intakes available at the moment."}
              </p>
              {isAuthenticated && (
                <Link to="/dashboard">
                  <Button className="btn-outline">Join Waitlist</Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredIntakes.map((intake) => (
                <motion.div
                  key={intake.id}
                  whileHover={{ x: 4 }}
                  className="card-premium flex flex-col md:flex-row md:items-center justify-between gap-4"
                  data-testid={`intake-${intake.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-heading text-xl text-charcoal">{intake.program_name}</h3>
                      <Badge className={
                        intake.is_fully_booked
                          ? "bg-red-600"
                          : intake.program_name?.includes('Foundations')
                            ? "bg-olive" 
                            : "bg-terracotta"
                      }>
                        {intake.is_fully_booked 
                          ? "Fully Booked" 
                          : intake.program_name?.includes('Foundations') 
                            ? "Limited Availability" 
                            : "Final Places Remaining"}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-stone">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Starts {new Date(intake.start_date).toLocaleDateString("en-AU", {
                          day: "numeric",
                          month: "long",
                          year: "numeric"
                        })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Timer className="w-4 h-4" />
                        {getDaysUntilText(intake.days_until_start)}
                      </span>
                      <span>{intake.day_of_week}s</span>
                      <span>4 weeks</span>
                    </div>
                  </div>
                  
                  {intake.is_fully_booked ? (
                    isAuthenticated ? (
                      <Link to={`/dashboard?waitlist=${intake.program_type}`}>
                        <Button variant="outline" className="btn-outline whitespace-nowrap border-terracotta text-terracotta hover:bg-terracotta hover:text-white" data-testid={`waitlist-${intake.id}`}>
                          Join Waitlist
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    ) : (
                      <Link to="/register">
                        <Button variant="outline" className="btn-outline whitespace-nowrap border-terracotta text-terracotta hover:bg-terracotta hover:text-white">
                          Register for Waitlist
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    )
                  ) : (
                    isAuthenticated ? (
                      <Link to={`/enroll/${intake.id}`}>
                        <Button className="btn-primary whitespace-nowrap" data-testid={`enroll-${intake.id}`}>
                          Enroll Now
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    ) : (
                      <Link to="/register">
                        <Button className="btn-primary whitespace-nowrap">
                          Register to Enroll
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    )
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default ProgramsPage;
